// @ts-check

// Vendor imports
const React = require('react')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')
const ViewportObserver = require('client-v2/common/utils/viewport-observer')

// IntersectionObserver options

/**
 * @typedef {Object} IntersectionObserverOptions
 * @property {number} threshold
 * @property {Element|null} [root]
 * @property {string} [rootMargin]
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
 */

/** @type {IntersectionObserverOptions} */
const FULLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS = { threshold: 1.00 }
/** @type {IntersectionObserverOptions} */
const PARTIALLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS = { threshold: 0.10 }

// ViewportObserver cache

/** @type {WeakMap<IntersectionObserverOptions, ViewportObserver>} */
const viewportObserverCache = new WeakMap()

/**
 *
 * @param {IntersectionObserverOptions} intersectionObserverOptions
 * @returns {ViewportObserver}
 */
function getViewportObserver(intersectionObserverOptions) {
  const cached = viewportObserverCache.get(intersectionObserverOptions)
  if (cached) {
    return cached
  }
  /**
   * @param {Element} _element
   * @param {() => void} onVisible
   */
  function callback(_element, onVisible) { onVisible() }
  const created = new ViewportObserver({
    intersectionObserverOptions,
    callback
  })
  viewportObserverCache.set(intersectionObserverOptions, created)
  return created
}

// Hook definition

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {IntersectionObserverOptions} options.intersectionObserverOptions
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean, () => void ]}
 */
function useViewportObserver({
  elementRef,
  initialIsVisible = false,
  intersectionObserverOptions,
  onVisible
}) {
  const [ isVisible, setVisible ] = React.useState(initialIsVisible)
  const resetVisible = React.useCallback(
    () => setVisible(false),
    [ setVisible ]
  )
  const observeCallback = React.useCallback(
    () => {
      if (!isVisible) {
        setVisible(true)
        if (onVisible) {
          onVisible()
        }
      }
    },
    [ isVisible, setVisible, onVisible ]
  )
  useClientEffect(
    () => {
      const el = elementRef.current
      if (!isVisible && el) {
        const viewportObserver = getViewportObserver(intersectionObserverOptions)
        viewportObserver.observe(el, observeCallback)
        return () => {
          viewportObserver.unobserve(el)
        }
      }
    },
    [ elementRef, intersectionObserverOptions, isVisible, observeCallback ]
  )
  return [ isVisible, resetVisible ]
}

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean, () => void ]}
 */
function useFullyVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: FULLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS
  })
}

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean, () => void ]}
 */
function usePartiallyVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: PARTIALLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS
  })
}

module.exports = {
  // Pre-defined IntersectionObserver options
  FULLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS,
  PARTIALLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS,
  // Hooks
  useFullyVisibleViewportObserver,
  usePartiallyVisibleViewportObserver,
  useViewportObserver
}
